import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Table, Loader, Tabs, Typography, Iconography } from '@lucid/lego';
import type { FilterState } from '@lucid/lego';
import { useStore } from '../../store/store';
import { useLabels } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';
import {
  useSearchVehicles,
  useGetLocation,
  useGetPrCodes,
  useGetCheckoutDetail,
  type ModelType,
} from '../../api/services/lupp/inventory/inventory';
import type { VehicleSearchFilters } from '../../api/services/lupp/inventory/dto.inventory';
import type { TransformedCheckoutDetail } from '../../api/services/lupp/inventory/transformers.checkout';
import {
  getInventoryColumns,
  DEFAULT_FILTER_STATE,
  createEmptyVehicleRow,
  type InventoryTableRow,
} from './utils';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  MIN_SEARCH_LENGTH,
  FILTER_KEY_MAPPING,
  COLUMN_TO_API_FIELD_MAPPING,
} from './constants';
import ConfigureShortCode from './ConfigureShortCode/ConfigureShortCode';
import Checkout from '../Checkout/Checkout';
import type { ProspectDetails } from '../Checkout/Checkout';
import styles from './Configurator.module.scss';

export type CheckoutParams = {
  batchNumber: string;
  uuid: string;
  type: 'new' | 'demo' | 'pre-owned';
};

export type ConfiguratorProps = {
  /**
   * Whether to show the "Configure with short code" tab
   * @default false
   */
  showConfigureTab?: boolean;
  /**
   * Custom data-testid for the component
   * @default 'configurator'
   */
  dataTestId?: string;
  /**
   * Callback when vehicle details view is shown/hidden
   */
  onDetailsViewChange?: (isShowingDetails: boolean) => void;
  /**
   * Checkout params from URL - when provided, show checkout view
   */
  checkoutParams?: CheckoutParams | null;
  /**
   * Callback when navigating to checkout view
   */
  onCheckoutNavigate?: (params: CheckoutParams) => void;
  /**
   * Callback when navigating to inventory with shortcode
   */
  onShortcodeNavigate?: (shortCode: string) => void;
  /**
   * Callback when navigating back from checkout to inventory
   */
  onCheckoutBack?: () => void;
  /**
   * Prospect details to pass to checkout for quote creation
   */
  prospectDetails?: ProspectDetails;
  /**
   * Callback when a quote is successfully created (for URL update)
   */
  onQuoteCreated?: (quoteId: string) => void;
};

/**
 * Configurator component for browsing and filtering available inventory vehicles
 * @component
 * @param props - Component props
 * @param props.showConfigureTab - Whether to show configure tab
 * @param props.dataTestId - Test ID for the component
 * @returns JSX.Element
 *
 * @example
 * <Configurator showConfigureTab onCheckoutNavigate={(params) => handleNavigate(params)} />
 */
const Configurator: React.FC<ConfiguratorProps> = ({
  showConfigureTab = false,
  dataTestId = 'configurator',
  onDetailsViewChange,
  checkoutParams,
  onCheckoutNavigate,
  onShortcodeNavigate,
  onCheckoutBack,
  prospectDetails,
  onQuoteCreated,
}) => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);

  // i18n labels for the configurator
  const { data: labels } = useLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTER_STATE);
  const [pendingTrims, setPendingTrims] = useState<string[] | undefined>(undefined);
  const [pendingModel, setPendingModel] = useState<string | undefined>(undefined);
  const [selectedVehicle, setSelectedVehicle] = useState<InventoryTableRow | null>(null);
  const [checkoutData, setCheckoutData] = useState<TransformedCheckoutDetail | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [activeTabId, setActiveTabId] = useState<string>('available-vehicles');
  const [isVinClickLoading, setIsVinClickLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchValue, setSearchValue] = useState('');
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const queryClient = useQueryClient();

  // Track if we've already fetched checkout for the current params
  const fetchedCheckoutRef = useRef<string | null>(null);

  // Reset to first page and refetch when locale changes
  useEffect(() => {
    setCurrentPage(0);
    queryClient.invalidateQueries({ queryKey: ['VEHICLE_SEARCH'] });
  }, [locale, queryClient]);

  // Checkout detail mutation
  const { mutateAsync: getCheckoutDetail, isPending: isLoadingDetails } = useGetCheckoutDetail();

  // Fetch user location from IP (always fetch - needed for checkout API)
  const { data: location, isLoading: isLocationLoading } = useGetLocation(true);

  // Fetch prCodes for both models on initial render (Air: 2025, Gravity: 2026)
  // This ensures fast switching between models without waiting for API
  const { data: airPrCodes } = useGetPrCodes('air', '2025', !checkoutParams);
  const { data: gravityPrCodes } = useGetPrCodes('gravity', '2026', !checkoutParams);

  // Determine current model from filter state or pending selection
  // Model filter is a radio button, so value is a string (not array)
  // Use pendingModel when filter panel is open, otherwise use applied filterState.model
  const appliedModel = filterState.model as string | undefined;
  const selectedModel = pendingModel ?? appliedModel;
  const isGravitySelected = selectedModel === 'GRAVITY';

  // Track the applied model type (for API calls - only changes when Apply is clicked)
  const isGravityApplied = appliedModel === 'GRAVITY';
  const appliedModelType: ModelType = isGravityApplied ? 'gravity' : 'air';

  // Use the appropriate prCodes based on selected model (updates immediately for filter options)
  const prCodes = isGravitySelected ? gravityPrCodes : airPrCodes;

  // Track previous APPLIED model to detect changes (only resets filters when Apply is clicked)
  const prevAppliedModelRef = useRef(appliedModelType);

  // Clear model-specific filters when APPLIED model changes (after clicking Apply)
  // This does NOT run when just switching models in the filter panel
  useEffect(() => {
    if (prevAppliedModelRef.current !== appliedModelType) {
      prevAppliedModelRef.current = appliedModelType;
      // Clear filters that are model-specific, but preserve the NEW model value
      // Convert internal model type back to filter value format
      const newModelValue = appliedModelType === 'gravity' ? 'GRAVITY' : 'AIR';
      setFilterState({
        model: newModelValue,
      });
      // Clear pending model and trims since they're model-specific
      setPendingModel(undefined);
      setPendingTrims(undefined);
      setCurrentPage(0);
    }
  }, [appliedModelType]);

  // Convert Table filterState to API filters (flat structure)
  const apiFilters = useMemo((): VehicleSearchFilters | undefined => {
    const filters: VehicleSearchFilters = {};

    Object.entries(filterState).forEach(([key, value]) => {
      if (!value) return;
      const apiKey = FILTER_KEY_MAPPING[key];
      if (!apiKey) return;

      if (Array.isArray(value) && value.length > 0) {
        (filters as Record<string, string>)[apiKey] = value.join(',');
      } else if (typeof value === 'string' && value) {
        (filters as Record<string, string>)[apiKey] = value;
      }
    });

    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterState]);

  // Combine apiFilters with search value
  const combinedFilters = useMemo((): VehicleSearchFilters | undefined => {
    // Add search value as vehicleIdentifier filter (searches VIN or Batch Number)
    if (searchValue.trim()) {
      const trimmedSearch = searchValue.trim();
      if (trimmedSearch.length >= MIN_SEARCH_LENGTH) {
        return {
          ...apiFilters,
          vehicleIdentifier: trimmedSearch,
        };
      }
    }

    return apiFilters;
  }, [apiFilters, searchValue]);

  // Build sort parameters for API
  const sortBy = useMemo(() => {
    if (!sortColumn) return undefined;
    return COLUMN_TO_API_FIELD_MAPPING[sortColumn] || sortColumn;
  }, [sortColumn]);

  // Search vehicles using the Auctus API (disabled when in checkout mode)
  const {
    data: searchResult,
    isLoading,
    error,
    isFetching,
  } = useSearchVehicles({
    page: currentPage,
    size: pageSize,
    filters: combinedFilters,
    sortBy,
    sortOrder: sortDirection,
    enabled: !checkoutParams, // Disable search when showing checkout
  });

  // Track if we've loaded data at least once
  const hasData = searchResult?.data !== undefined;

  const tableData = useMemo(() => {
    if (!searchResult?.data) return [];
    return searchResult.data;
  }, [searchResult]);

  // Build columns with dynamic filter options from prCodes
  // Use pendingTrims when filter panel is open, otherwise use applied filterState.trim
  const appliedTrims = filterState.trim as string[] | undefined;
  const selectedTrims = pendingTrims ?? appliedTrims;
  const columns = useMemo(
    () => getInventoryColumns(prCodes, isGravitySelected, selectedTrims, labels, locale),
    [prCodes, isGravitySelected, selectedTrims, labels, locale]
  );

  // Handle checkout params from URL on mount/change
  useEffect(() => {
    const fetchCheckoutFromUrl = async () => {
      if (!checkoutParams) return;

      // Wait for location to load before fetching checkout details
      if (isLocationLoading) return;

      // Create a unique key for these checkout params
      const paramsKey = `${checkoutParams.batchNumber}-${checkoutParams.uuid}`;

      // Skip if we've already fetched for these params
      if (fetchedCheckoutRef.current === paramsKey) {
        return;
      }

      // Mark as fetched immediately to prevent duplicate calls
      fetchedCheckoutRef.current = paramsKey;

      onDetailsViewChange?.(true);
      setCheckoutData(null);
      try {
        const details = await getCheckoutDetail({
          batchNumber: checkoutParams.batchNumber,
          uuid: checkoutParams.uuid,
          type: checkoutParams.type,
          zipState: prospectDetails?.stateProvince || location?.region || 'CA',
        });
        setCheckoutData(details);
        // Create a minimal vehicle object from checkout params
        setSelectedVehicle(
          createEmptyVehicleRow({
            batchNumber: checkoutParams.batchNumber,
            uuid: checkoutParams.uuid,
            condition: checkoutParams.type,
          })
        );
      } catch (err) {
        // Reset ref on error so it can be retried
        fetchedCheckoutRef.current = null;
        // Reset state to prevent infinite loader
        setSelectedVehicle(null);
        setCheckoutError('Failed to load vehicle details. Please try again.');
        onDetailsViewChange?.(false);
      }
    };
    fetchCheckoutFromUrl();
  }, [
    checkoutParams,
    getCheckoutDetail,
    location?.region,
    onDetailsViewChange,
    isLocationLoading,
    prospectDetails?.stateProvince,
  ]);

  const handleVinClick = useCallback(
    async (vehicle: InventoryTableRow) => {
      // Clear any previous error
      setCheckoutError(null);

      // Use URL-based navigation if callback provided
      if (onCheckoutNavigate) {
        // Call API first, only navigate on success
        setIsVinClickLoading(true);
        try {
          const details = await getCheckoutDetail({
            batchNumber: vehicle.batchNumber,
            uuid: vehicle.uuid,
            type: vehicle.condition,
            zipState: prospectDetails?.stateProvince || location?.region || 'CA',
          });
          // Store checkout data and mark as fetched before navigation
          // This prevents the URL-based useEffect from re-fetching
          const paramsKey = `${vehicle.batchNumber}-${vehicle.uuid}`;
          fetchedCheckoutRef.current = paramsKey;
          setCheckoutData(details);
          setSelectedVehicle(vehicle);
          onDetailsViewChange?.(true);
          // Navigate after data is ready
          onCheckoutNavigate({
            batchNumber: vehicle.batchNumber,
            uuid: vehicle.uuid,
            type: vehicle.condition,
          });
        } catch (err) {
          setCheckoutError('Failed to load vehicle details. Please try again.');
        } finally {
          setIsVinClickLoading(false);
        }
        return;
      }

      // Fallback to local state management
      setSelectedVehicle(vehicle);
      setCheckoutData(null);
      onDetailsViewChange?.(true);
      try {
        const details = await getCheckoutDetail({
          batchNumber: vehicle.batchNumber,
          uuid: vehicle.uuid,
          type: vehicle.condition,
          zipState: prospectDetails?.stateProvince || location?.region || 'CA',
        });
        setCheckoutData(details);
      } catch (err) {
        setCheckoutError('Failed to load vehicle details. Please try again.');
        setSelectedVehicle(null);
        onDetailsViewChange?.(false);
      }
    },
    [
      getCheckoutDetail,
      location?.region,
      onDetailsViewChange,
      onCheckoutNavigate,
      prospectDetails?.stateProvince,
    ]
  );

  // Add VIN click handler and web link to columns
  const columnsWithClickHandlers = useMemo(() => {
    return columns.map((col) => {
      if (col.key === 'vin') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => (
            <Typography
              variant="body"
              size="small"
              theme={theme}
              color="link"
              onClick={() => handleVinClick(record)}
              className={styles.clickableVin}
            >
              {record.vin}
            </Typography>
          ),
        };
      }
      if (col.key === 'batchNumber') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => (
            <Typography
              variant="body"
              size="small"
              theme={theme}
              color="link"
              onClick={() => handleVinClick(record)}
              className={styles.clickableVin}
            >
              {record.batchNumber}
            </Typography>
          ),
        };
      }
      if (col.key === 'webLink') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => {
            const webUrl = record.webLink;
            if (!webUrl) return null;
            return (
              <Box
                className={styles.webLinkIcon}
                onClick={() => window.open(webUrl, '_blank', 'noopener,noreferrer')}
              >
                <Iconography category="global" name="link" color="link" theme={theme} />
              </Box>
            );
          },
        };
      }
      return col;
    });
  }, [columns, theme, handleVinClick]);

  const handleFilterChange = useCallback((newFilterState: FilterState) => {
    setFilterState(newFilterState);
    setPendingTrims(undefined); // Clear pending trims when filters are applied
    setPendingModel(undefined); // Clear pending model when filters are applied
    setCurrentPage(0); // Reset to first page when filters change
  }, []);

  const handlePendingFilterChange = useCallback((pendingFilters: FilterState) => {
    // Update pending trims so filter options update immediately
    const trimValue = pendingFilters.trim;
    const newPendingTrims = Array.isArray(trimValue) ? trimValue : undefined;
    setPendingTrims(newPendingTrims);

    // Update pending model so prCodes switch immediately when model changes
    const modelValue = pendingFilters.model;
    const newPendingModel = typeof modelValue === 'string' ? modelValue : undefined;
    setPendingModel(newPendingModel);
  }, []);

  /**
   * Handles successful shortcode checkout - sets checkout data and navigates to checkout view
   */
  const handleShortcodeCheckoutSuccess = (data: TransformedCheckoutDetail, shortCode: string) => {
    // Store checkout data
    setCheckoutData(data);

    // Create a vehicle row with available data from checkout
    setSelectedVehicle(
      createEmptyVehicleRow({
        vin: data.vin || '',
        batchNumber: data.vinBatchNumber || '',
        availability: data.availability || '',
        model: data.model || '',
        trim: data.trim || '',
        year: data.modelYear || '',
        shortCode,
        condition: (data.type as 'new' | 'demo' | 'pre-owned') || 'new',
        price: data.pricing?.total || 0,
      })
    );

    onDetailsViewChange?.(true);
    setActiveTabId('configure-short-code');

    // Navigate to inventory with shortcode in URL
    onShortcodeNavigate?.(shortCode);
  };

  const handleBackToInventory = () => {
    setSelectedVehicle(null);
    setCheckoutData(null);
    onDetailsViewChange?.(false);
    onCheckoutBack?.();
  };

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  if (error) {
    return (
      <Box data-testid={`${dataTestId}-error`}>
        <Box pt={8}>{labels?.['configurator.error.loading'] ?? 'Error loading inventory data'}</Box>
      </Box>
    );
  }

  // Show checkout view when checkout params are in URL (even if API fails)
  // This ensures users see the checkout UI with an error message rather than the inventory table
  if (checkoutParams) {
    return (
      <Checkout
        vehicle={selectedVehicle}
        checkoutData={checkoutData}
        isLoading={isLoadingDetails || isLocationLoading}
        onBack={handleBackToInventory}
        dataTestId={`${dataTestId}-checkout`}
        prospectDetails={prospectDetails}
        error={checkoutError}
        onQuoteCreated={onQuoteCreated}
      />
    );
  }

  // Show vehicle details view when a vehicle is selected (local state navigation)
  if (selectedVehicle) {
    return (
      <Checkout
        vehicle={selectedVehicle}
        checkoutData={checkoutData}
        isLoading={isLoadingDetails}
        onBack={handleBackToInventory}
        dataTestId={`${dataTestId}-checkout`}
        prospectDetails={prospectDetails}
        onQuoteCreated={onQuoteCreated}
      />
    );
  }

  const availableVehiclesTab = {
    id: 'available-vehicles',
    label: labels?.['configurator.tab.availableVehicles'] ?? 'Available Vehicles',
    content: (
      <div className={styles.inventoryBrowserContainer}>
        {checkoutError && (
          <Box className={styles.errorBanner}>
            <Typography variant="body" size="small" color="white">
              {checkoutError}
            </Typography>
          </Box>
        )}
        <div className={styles.tableWrapper}>
          {(isLoading || isLocationLoading) && !hasData ? (
            <Box className={styles.loaderWrapper}>
              <Loader theme={invertedTheme} />
            </Box>
          ) : (
            <div className={styles.tableContentWrapper}>
              {(isFetching || isVinClickLoading) && (
                <div className={styles.fetchingOverlay}>
                  <Loader theme={invertedTheme} />
                </div>
              )}
              <Table
                columns={columnsWithClickHandlers}
                data={tableData}
                theme={invertedTheme}
                selection={{ enabled: false }}
                columnConfig={{ enabled: true }}
                filter={{
                  enabled: true,
                  state: filterState,
                  onChange: handleFilterChange,
                  onPendingChange: handlePendingFilterChange,
                  dependentFilters: {
                    // When model changes, reset trim and all trim-dependent filters
                    model: [
                      'trim',
                      'exteriorColor',
                      'interiorColor',
                      'wheels',
                      'appearance',
                      'adas',
                      'packages',
                    ],
                    // When trim changes, reset all trim-dependent filters
                    trim: [
                      'exteriorColor',
                      'interiorColor',
                      'wheels',
                      'appearance',
                      'adas',
                      'packages',
                    ],
                  },
                }}
                pagination={{
                  enabled: true,
                  pageSize: pageSize,
                  current: currentPage,
                  total: searchResult?.totalElements ?? 0,
                  onChange: (page) => setCurrentPage(page),
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  onPageSizeChange: (newSize) => {
                    setPageSize(newSize);
                    setCurrentPage(0);
                  },
                }}
                search={{
                  placeholder:
                    labels?.['configurator.search.placeholder'] ??
                    'Search with VIN or Batch Number',
                  value: searchValue,
                  onChange: (value) => {
                    setSearchValue(value);
                    setCurrentPage(0);
                    // Force refetch when search is cleared
                    if (value === '') {
                      queryClient.invalidateQueries({ queryKey: ['VEHICLE_SEARCH'] });
                    }
                  },
                }}
                sort={{
                  column: sortColumn,
                  direction: sortDirection,
                  onSort: (column, direction) => {
                    setSortColumn(column);
                    setSortDirection(direction);
                    setCurrentPage(0);
                  },
                }}
                emptyState={{
                  message:
                    labels?.['configurator.emptyState.message'] ?? 'No vehicles match your filters',
                }}
                labels={{
                  manageColumns: labels?.['configurator.table.manageColumns'] ?? 'Manage Columns',
                  filter: labels?.['configurator.table.filter'] ?? 'Filter',
                  filterWithCount:
                    labels?.['configurator.table.filterWithCount'] ?? 'Filter ({{count}})',
                  selectAll: labels?.['configurator.table.selectAll'] ?? 'Select All',
                  clearAll: labels?.['configurator.table.clearAll'] ?? 'Clear All',
                  columnsVisible:
                    labels?.['configurator.table.columnsVisible'] ??
                    '{{visible}} of {{total}} columns visible',
                  showingItems:
                    labels?.['configurator.table.showingItems'] ??
                    'Showing {{start}}-{{end}} of {{total}} Items',
                  pageOf: labels?.['configurator.table.pageOf'] ?? '{{current}} of {{total}}',
                  save: labels?.['configurator.table.save'] ?? 'Save',
                  apply: labels?.['configurator.table.apply'] ?? 'Apply',
                  filtersTitle: labels?.['configurator.table.filtersTitle'] ?? 'Filters',
                }}
                gaComponent={`${dataTestId}-table`}
                dataTestId={`${dataTestId}-table`}
              />
            </div>
          )}
        </div>
      </div>
    ),
  };

  const configureShortCodeTab = {
    id: 'configure-short-code',
    label: labels?.['configurator.tab.configureShortCode'] ?? 'Configure with short code',
    content: (
      <ConfigureShortCode onCheckoutSuccess={handleShortcodeCheckoutSuccess} labels={labels} />
    ),
  };

  const tabs = showConfigureTab
    ? [availableVehiclesTab, configureShortCodeTab]
    : [availableVehiclesTab];

  return (
    <Box data-testid={dataTestId}>
      <Tabs
        dataTestId={`${dataTestId}-tabs`}
        theme={theme}
        activeTab={activeTabId}
        onTabChange={setActiveTabId}
        tabs={tabs}
      />
    </Box>
  );
};

export default Configurator;
